import React, { useState } from 'react';

import TruvBridge from '@truv/react';
import { useSelector } from 'react-redux';

import { LoadingType, RootState, setLoadingState } from 'actions';
import { setLoanPortal } from 'actions/dashboardPortal';
import { BankAccountsStatus } from 'enums/BankAccountsStatus';
import { useAppSelector } from 'hooks/useAppSelector';
import { InteractiveBoxStatus } from 'components/InteractiveBox/InteractiveBox';
import InteractiveBox from 'components/InteractiveBox';
import { BorrowerRole } from 'enums/BorrowerRole';
import * as notificationManager from 'notifications';
import { FailureNotificationType } from 'notifications';
import { useAppDispatch } from 'hooks/useAppDispatch';
import { getApplicationInfoForDashboardThunk } from 'thunks/getApplicationInfoForDashboard';
import { truvApi } from 'api/TruvApi';

import styles from './FinancialAccounts.module.scss';
import Section from '../Sections/Section';
import SubSection from '../Sections/SubSection';
import { SubSectionState } from '../Sections/SubSection/SubSection';

const VERIFICATION_ERROR_TYPE = 'ERROR';
const VERIFICATION_NO_DATA_ERROR_CODE = 'NO_DATA';

const FinancialAccounts = () => {
  const dispatch = useAppDispatch();

  const { applicationId } = useAppSelector((state) => state.application);
  const { borrowerId } = useSelector((state: RootState) => state.borrower);
  const { financialAccountAggregationAttemptComplete, fundingInformationComplete } = useAppSelector(
    (state) => state.dashboardPortal,
  );

  const [currentToken, setCurrentToken] = useState<string>('');
  const [isVerificationInProgress, setIsVerificationInProgress] = useState<boolean>(false);
  const [isVerificationWidgetOpened, setIsVerificationWidgetOpened] = useState<boolean>(false);

  const getBridgeTokenValue = async () => {
    try {
      setIsVerificationInProgress(true);

      const { token } = await truvApi.createBridgeToken({
        borrowerId: borrowerId as string,
        applicationId: applicationId as string,
        borrowerType: BorrowerRole.Borrower,
        socialSecurityBenefitType: null,
        useFinancialAccountAggregation: true,
      });

      setCurrentToken(token);
      setIsVerificationWidgetOpened(true);
      setIsVerificationInProgress(false);
    } catch (error) {
      setIsVerificationWidgetOpened(false);

      notificationManager.notifyFailure(FailureNotificationType.UnknownError);
    }
  };

  const handleSuccessVerification = async () => {
    try {
      dispatch(
        setLoadingState({
          type: LoadingType.GetBankAccounts,
          loading: true,
        }),
      );

      setIsVerificationWidgetOpened(false);

      await truvApi.completeFinancialAccountAggregation({
        applicationId: applicationId!,
      });

      dispatch(
        setLoanPortal({
          bankAccountsStatus: BankAccountsStatus.PendingFinancialAccounts,
          financialAccountAggregationAttemptComplete: true,
        }),
      );
    } catch (error) {
      dispatch(setLoanPortal({ financialAccountAggregationAttemptComplete: false }));

      notificationManager.notifyFailure(FailureNotificationType.UnknownError);
    } finally {
      dispatch(
        setLoadingState({
          type: LoadingType.GetBankAccounts,
          loading: false,
        }),
      );
    }
  };

  const handleNoDataEvent = async (type: string, payload?: Record<string, any>) => {
    if (type === VERIFICATION_ERROR_TYPE && payload?.error?.error_code === VERIFICATION_NO_DATA_ERROR_CODE) {
      try {
        dispatch(
          setLoadingState({
            type: LoadingType.GetBankAccounts,
            loading: true,
          }),
        );

        await truvApi.saveVerificationError({
          applicationId: applicationId!,
        });

        await dispatch(getApplicationInfoForDashboardThunk(borrowerId!)).unwrap();
      } catch (error) {
        dispatch(setLoanPortal({ financialAccountAggregationAttemptComplete: false }));

        notificationManager.notifyFailure(FailureNotificationType.UnknownError);
      } finally {
        dispatch(
          setLoadingState({
            type: LoadingType.GetBankAccounts,
            loading: false,
          }),
        );
      }
    }
  };

  const getVerificationBoxClickHandler = () => {
    if (isVerificationInProgress || financialAccountAggregationAttemptComplete) {
      return;
    }

    return getBridgeTokenValue();
  };

  const fundingSubSectionState: SubSectionState = {
    isCompleted: fundingInformationComplete,
    isExist: true,
  };

  return (
    <>
      <Section title={'Funding Information'} subSectionStates={[fundingSubSectionState]}>
        <SubSection state={fundingSubSectionState}>
          <div className={styles.sectionTitle}>
            After you connect to your Financial Institution your bank account will be used for funding your loan
            proceeds and autopay, if you have elected for autopay.
          </div>
          <div className={styles.mainHeader}>
            <InteractiveBox
              asyncLoading
              status={
                financialAccountAggregationAttemptComplete
                  ? InteractiveBoxStatus.Completed
                  : InteractiveBoxStatus.InitialConnect
              }
              title="Connect To My Bank Account"
              onClick={getVerificationBoxClickHandler}
            />
            <TruvBridge
              bridgeToken={currentToken}
              onClose={() => setIsVerificationWidgetOpened(false)}
              onSuccess={() => handleSuccessVerification()}
              isOpened={isVerificationWidgetOpened}
              // @ts-ignore
              onEvent={handleNoDataEvent}
            />
          </div>
        </SubSection>
      </Section>
    </>
  );
};

export default FinancialAccounts;
